import { CREDIT_ACCOUNT_MIGRATION_STATUS, CREDIT_ACCOUNT_STATUS } from 'constants/creditAccount';
import moment from 'moment';

const toCamelCase = string => string?.charAt(0).toLowerCase() + string?.slice(1);

const creditAccountMigrationConfigManager = ({ corporation, override = null }) => {
  if (!corporation) return null;

  const creditAccountMigrationStatus = toCamelCase(corporation.creditAccountMigrationStatus);
  const creditAccountStatus = toCamelCase(corporation.creditAccountStatus);

  const isStatus = (status, expected) => status === expected;
  const isAnyStatus = (status, expectedStatuses) => expectedStatuses.includes(status);

  const isCreditAccountMigrationNew = isStatus(
    creditAccountMigrationStatus,
    CREDIT_ACCOUNT_MIGRATION_STATUS.NEW,
  );

  const isCreditAccountMigrationStarted =
    !!creditAccountStatus &&
    isStatus(creditAccountMigrationStatus, CREDIT_ACCOUNT_MIGRATION_STATUS.STARTED);

  const isCreditAccountMigrationNotStarted =
    !creditAccountMigrationStatus ||
    creditAccountMigrationStatus === CREDIT_ACCOUNT_MIGRATION_STATUS.NOT_STARTED;

  const isCreditAccountStatusActive = isStatus(creditAccountStatus, CREDIT_ACCOUNT_STATUS.ACTIVE);
  const isCreditAccountStatusPending = isStatus(creditAccountStatus, CREDIT_ACCOUNT_STATUS.PENDING);
  const isCreditAccountStatusRejected = isStatus(
    creditAccountStatus,
    CREDIT_ACCOUNT_STATUS.REJECTED,
  );

  const isCreditAccountStatusOther =
    !!creditAccountStatus && !Object.values(CREDIT_ACCOUNT_STATUS).includes(creditAccountStatus);

  const isNewBadgeDisplayed = isCreditAccountMigrationStarted && isCreditAccountStatusPending;
  const isCreditAccountPageBlocked =
    !isCreditAccountMigrationStarted && !isCreditAccountMigrationNew;
  const isInvoicesPageBlocked =
    isCreditAccountMigrationNotStarted ||
    isAnyStatus(creditAccountStatus, [
      CREDIT_ACCOUNT_STATUS.CLOSED,
      CREDIT_ACCOUNT_STATUS.REJECTED,
    ]);
  const isCreditAccountPendingBannerVisible = isCreditAccountStatusPending;
  const isCreditAccountRejectedBannerVisible = isCreditAccountStatusRejected;
  const isCreditAccountBannerVisible =
    isCreditAccountMigrationStarted && !isCreditAccountStatusRejected;
  const isCreditAccountInfoModalVisible =
    override?.isCreditAccountInfoModalVisible ??
    (isCreditAccountMigrationStarted && !creditAccountStatus);

  const isPaymentMethodsPageBlocked =
    (isCreditAccountMigrationNew &&
      !isCreditAccountStatusRejected &&
      !isCreditAccountStatusPending) ||
    (isCreditAccountMigrationStarted &&
      (isCreditAccountStatusActive || isCreditAccountStatusOther));

  const isBillingPageReadonly =
    isAnyStatus(creditAccountStatus, [
      CREDIT_ACCOUNT_STATUS.ACTIVE,
      CREDIT_ACCOUNT_STATUS.ON_HOLD,
      CREDIT_ACCOUNT_STATUS.PENDING,
    ]) || isCreditAccountStatusOther;

  const isCreditAccountStepActive =
    (isCreditAccountMigrationNew && !isCreditAccountStatusRejected) ||
    (isCreditAccountMigrationStarted && !isCreditAccountStatusRejected);

  const isCreditAccountActivationDateTime = moment(corporation.creditAccountActivationDateTime);
  const endOfNextMonthFromActivation = isCreditAccountActivationDateTime
    .clone()
    .add(1, 'month')
    .endOf('month');
  const today = moment();

  const isReplaceMonthlyStatementsBannerVisible = Boolean(
    corporation.creditAccountActivationDateTime &&
      isCreditAccountStatusActive &&
      !isCreditAccountMigrationNew &&
      today.isBefore(endOfNextMonthFromActivation),
  );

  return {
    isCreditAccountInfoModalVisible,
    paymentMethods: {
      isPageBlocked: isPaymentMethodsPageBlocked,
      isCreditAccountBannerVisible,
    },
    creditAccount: {
      isNewBadgeDisplayed,
      isPageBlocked: isCreditAccountPageBlocked,
      isPendingBannerVisible: isCreditAccountPendingBannerVisible,
      isRejectedBannerVisible: isCreditAccountRejectedBannerVisible,
    },
    invoices: {
      isPageBlocked: isInvoicesPageBlocked,
    },
    billing: {
      isReadonly: isBillingPageReadonly,
    },
    onboarding: {
      isCreditAccountStepActive,
    },
    monthlyStatements: {
      isReplaceMonthlyStatementsBannerVisible,
    },
  };
};

export default creditAccountMigrationConfigManager;
